import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Trash2, Tag, X } from "lucide-react";
import { resolveImage } from "../lib/image";
import { fmt } from "../lib/money";
import ActiveAffiliateWidget from "../components/ActiveAffiliateWidget";
import { useAuth } from "../context/AuthContext";
import { canSeeAffiliateUI } from "../lib/permissions";

export default function Cart() {
  const {
    items, updateLine, removeLine, subtotal,
    discounts, applyCode, removeCode, discountTotal,
  } = useCart();
  const { user } = useAuth();
  const showAffiliate = canSeeAffiliateUI(user);
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);

  if (items.length === 0) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 text-center">
        <h1 className="font-display font-black text-5xl tracking-tight">Your cart is empty.</h1>
        <p className="mt-4 text-neutral-600">Time to fill it with things worth owning.</p>
        <Link to="/products" className="inline-flex mt-8 bg-[#0033FF] hover:bg-[#0022CC] text-white px-6 h-12 items-center font-semibold">
          Browse the marketplace
        </Link>
      </div>
    );
  }

  const freeShippingUnlocked = discounts.some((d) =>
    ["free_shipping", "fixed_plus_free_shipping", "percent_plus_free_shipping"].includes(d.discount_type)
  );
  const shippingPercentOff = Math.max(0, ...discounts.filter((d) => d.discount_type === "shipping_percent").map((d) => d.discount_value || 0));
  let shippingBase = freeShippingUnlocked || subtotal >= 20000 ? 0 : 2000;
  let shipping = shippingPercentOff > 0 ? Math.max(0, Math.round(shippingBase * (1 - Math.min(shippingPercentOff, 100) / 100))) : shippingBase;
  // Mirror backend safety net: if items net to zero after discounts, auto-waive shipping.
  const shippingAutoWaived = shipping > 0 && subtotal - discountTotal <= 0;
  if (shippingAutoWaived) shipping = 0;
  const total = Math.max(subtotal + shipping - discountTotal, 0);

  const onApply = async (e) => {
    e.preventDefault();
    if (applying) return;
    setApplying(true);
    const ok = await applyCode(code);
    if (ok) setCode("");
    setApplying(false);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10 w-full">
      <h1 className="font-display font-black text-3xl sm:text-5xl lg:text-6xl tracking-tight">Cart</h1>
      <div className="grid lg:grid-cols-[1fr_380px] gap-6 sm:gap-8 lg:gap-12 mt-6 sm:mt-10 min-w-0">
        <div className="border-t border-neutral-200 min-w-0">
          {items.map((it) => (
            <div
              key={it.line_id}
              data-testid={`cart-item-${it.line_id}`}
              className="flex gap-3 sm:gap-5 py-5 sm:py-6 border-b border-neutral-200 min-w-0"
            >
              <Link to={`/products/${it.product_id}`} className="w-20 h-20 sm:w-24 sm:h-24 shrink-0 bg-neutral-100 border border-neutral-200 overflow-hidden">
                {it.image && <img src={resolveImage(it.image)} alt={it.title} className="w-full h-full object-cover" />}
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/products/${it.product_id}`} className="font-display font-bold text-base sm:text-lg hover:text-[#0033FF] break-words">
                  {it.title}
                </Link>
                {it.seller_id && it.seller_name && (
                  <Link
                    to={`/shop/${it.seller_id}`}
                    data-testid={`cart-seller-link-${it.line_id}`}
                    className="block text-[11px] uppercase tracking-widest mt-1 text-[#FF2A00] font-semibold hover:underline"
                  >
                    Sold by <span className="italic">{it.seller_name}</span> →
                  </Link>
                )}
                <div className="text-sm text-neutral-500 mt-1">{fmt(it.price)} each</div>
                {(it.size || it.color) && (
                  <div className="flex gap-2 mt-2 text-[11px] uppercase tracking-wider">
                    {it.size && <span className="border border-neutral-300 px-2 py-0.5">Size: {it.size}</span>}
                    {it.color && <span className="border border-neutral-300 px-2 py-0.5">Color: {it.color}</span>}
                  </div>
                )}
                <div className="flex items-center gap-3 mt-3">
                  <div className="flex items-center border border-black">
                    <button
                      data-testid={`cart-dec-${it.line_id}`}
                      onClick={() => updateLine(it.line_id, Math.max(1, it.quantity - 1))}
                      className="w-9 h-9 font-bold"
                    >−</button>
                    <div className="w-9 h-9 grid place-items-center text-sm font-semibold">{it.quantity}</div>
                    <button
                      data-testid={`cart-inc-${it.line_id}`}
                      onClick={() => updateLine(it.line_id, it.quantity + 1)}
                      className="w-9 h-9 font-bold"
                    >+</button>
                  </div>
                  <button
                    data-testid={`cart-remove-${it.line_id}`}
                    onClick={() => removeLine(it.line_id)}
                    className="text-sm text-neutral-500 hover:text-[#FF2A00] flex items-center gap-1"
                  >
                    <Trash2 className="w-4 h-4" /> Remove
                  </button>
                </div>
              </div>
              <div className="font-display font-black text-base sm:text-lg shrink-0 text-right">{fmt(it.price * it.quantity)}</div>
            </div>
          ))}
        </div>

        <aside className="bg-neutral-50 border border-neutral-200 p-4 sm:p-6 h-fit min-w-0">
          <h2 className="font-display font-black text-2xl mb-4 sm:mb-6">Summary</h2>

          {showAffiliate && <ActiveAffiliateWidget prominent />}
          <form onSubmit={onApply} className="flex gap-2 mb-4">
            <Input
              data-testid="cart-discount-input"
              placeholder="Discount code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="rounded-none border-black h-11 font-mono tracking-wider"
            />
            <Button
              data-testid="cart-apply-discount-btn"
              type="submit"
              disabled={applying || !code.trim()}
              className="rounded-none bg-black hover:bg-neutral-800 h-11 px-4 font-semibold"
            >
              {applying ? "…" : "Apply"}
            </Button>
          </form>

          {/* Applied codes */}
          {discounts.length > 0 && (
            <ul data-testid="cart-applied-discounts" className="mb-4 space-y-2">
              {discounts.map((d) => (
                <li
                  key={d.code}
                  data-testid={`cart-discount-${d.code}`}
                  className="flex items-center justify-between gap-2 bg-white border border-black px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-xs">
                      <Tag className="w-3 h-3 text-[#0033FF] shrink-0" />
                      <code className="font-display font-bold tracking-tight">{d.code}</code>
                      <span className="text-neutral-500 truncate">· {d.seller_name}</span>
                    </div>
                    <div className="text-[11px] text-[#00A355] font-semibold mt-0.5">
                      {d.discount_type === "free_shipping" ? "Free shipping" : d.discount_type === "shipping_percent" ? `${d.discount_value}% off shipping` : `− ${fmt(d.discount_amount)}`}
                    </div>
                  </div>
                  <button
                    data-testid={`cart-remove-discount-${d.code}`}
                    type="button"
                    onClick={() => removeCode(d.code)}
                    className="w-7 h-7 grid place-items-center border border-black hover:bg-[#FF2A00] hover:text-white hover:border-[#FF2A00] shrink-0"
                    aria-label={`Remove code ${d.code}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span data-testid="cart-subtotal">{fmt(subtotal)}</span></div>
            {discountTotal > 0 && (
              <div className="flex justify-between text-[#00A355] font-semibold">
                <span>Discounts</span>
                <span data-testid="cart-discount-total">− {fmt(discountTotal)}</span>
              </div>
            )}
            <div className="flex justify-between items-center gap-2">
              <span>Shipping</span>
              <span className="text-right">
                {shipping === 0 ? "Free" : fmt(shipping)}
                {shippingAutoWaived && (
                  <span data-testid="cart-shipping-auto-waived" className="block text-[10px] text-[#00A355] font-semibold uppercase tracking-wide">
                    Auto-applied
                  </span>
                )}
              </span>
            </div>
            <div className="flex justify-between pt-3 border-t border-neutral-300 font-bold text-base">
              <span>Total</span><span data-testid="cart-total">{fmt(total)}</span>
            </div>
          </div>
          <Button
            data-testid="checkout-btn"
            onClick={() => navigate("/checkout")}
            className="w-full mt-6 h-12 rounded-none bg-[#0033FF] hover:bg-[#0022CC] font-semibold"
          >
            Checkout
          </Button>
          <p className="text-xs text-neutral-500 mt-3">Free shipping on orders over {fmt(20000)}. Otherwise {fmt(2000)}.</p>
        </aside>
      </div>
    </div>
  );
}
